import { type FC, type PropsWithChildren, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { UserInfoContext } from '@/contexts/UserInfoContext';

/**
 * Route guard component that blocks PLG (free tier) users from accessing paid-plan features.
 * If the user is on a PLG plan, they are redirected to the home page.
 * This guard only blocks access for PLG users (isPlg === true).
 *
 * Usage:
 * ```tsx
 * <Route path="/admin/webhooks" element={
 *   <RequirePaidPlan>
 *     <WebhooksRoute />
 *   </RequirePaidPlan>
 * } />
 * ```
 */
export const RequirePaidPlan: FC<PropsWithChildren> = ({ children }) => {
  const { userInfo } = useContext(UserInfoContext);
  const navigate = useNavigate();
  const isPlg = !!userInfo?.isPlg;

  useEffect(() => {
    // Only block access if user is on PLG (free tier)
    if (isPlg) {
      navigate('/', { replace: true });
    }
  }, [isPlg, navigate]);

  // Block access for PLG users
  if (isPlg) {
    return null;
  }

  return <>{children}</>;
};
